import { forwardRef, memo, useCallback, useState } from 'react'
import { useNavigate, useLocation, createSearchParams } from 'react-router-dom'
import clsx from 'clsx'
import { useAppSelector } from '@/redux/core/core.hooks'
import { selectAuthToken } from '@/redux/auth'
import { Button, CurrencyAndLocation } from '@/components/Inputs'
import { Box } from '@/components/Layout'
import { headerActionsStyles as styles } from './HeaderActions.style'
import { useTranslations } from '@/core/translation'
import { ROUTES, usePackageInfo } from '@/utils'
import { IconButton } from '@mui/material'
import { ShoppingCartIcon } from '@/assets'
import { Text } from '@/components/Data'
import { headerClasses } from '../Header.data'
import CartDrawer from '../CartDrawer'
import { HeaderActionsProps } from './HeaderActions.types'

const HeaderActions = forwardRef<HTMLDivElement, HeaderActionsProps>(
  ({ hidePackageInfo, packageInfo, ...props }, ref) => {
    const { productCount } = usePackageInfo()

    const t = useTranslations('header')
    const navigate = useNavigate()
    const location = useLocation()
    const [cartOpen, setCartOpen] = useState(false)
    const authToken = useAppSelector(selectAuthToken)

    const onCartOpen = useCallback(() => setCartOpen(true), [])

    const onSignIn = useCallback(() => {
      if (authToken) navigate(ROUTES.USER.PROFILE)
      else
        navigate({
          pathname: ROUTES.LOGIN,
          search: createSearchParams({
            redirect: `${location.pathname}${location.search}`,
          }).toString(),
        })
    }, [authToken, navigate, location.pathname, location.search])

    return (
      <Box
        ref={ref}
        className={clsx({
          [headerClasses.actionWrapper]: true,
          [headerClasses.hasPackage]: packageInfo,
        })}
        sx={styles.actionWrapper}
        {...props}>
        {!packageInfo && (
          <>
            <CurrencyAndLocation />
            <Button
              sx={styles.button}
              textProps={{ sx: styles.buttonText }}
              color="transparentOutlined"
              size="small"
              onClick={onSignIn}
              noBorder>
              {authToken ? t('actions.profile') : t('actions.signIn')}
            </Button>
          </>
        )}

        {packageInfo && !hidePackageInfo && (
          <>
            <Box sx={styles.desktopActions}>
              <CurrencyAndLocation />
              <Button
                sx={styles.button}
                textProps={{ sx: styles.buttonText }}
                color="transparentOutlined"
                size="small"
                onClick={() => navigate(ROUTES.HOME)}
                noBorder>
                {t('actions.cancel')}
              </Button>
            </Box>
            <IconButton sx={styles.cartButton} onClick={onCartOpen}>
              <ShoppingCartIcon className="icon" />
              {productCount > 0 && (
                <Box sx={styles.cartCounterWrapper}>
                  <Text sx={styles.cartCounter} component={'span'}>
                    {productCount}
                  </Text>
                </Box>
              )}
            </IconButton>
          </>
        )}
        <CartDrawer open={cartOpen} onClose={() => setCartOpen(false)} />
      </Box>
    )
  },
)

HeaderActions.displayName = 'HeaderActions'

export default memo(HeaderActions)
